import React, { useRef, useEffect, useCallback, useState } from 'react'
import { Row, Col, Button, Card, Toast } from 'react-bootstrap'
import { useProject } from './NewProjectContext'
import { useCreateProjectMutation } from '../../services/projectsApi'
import { toTitleCase } from '../../utils'
import { Check, AlertCircle } from 'lucide-react'

interface CPCreateProjectStepProps {
  onHide: () => void
}

const CPCreateProjectStep: React.FC<CPCreateProjectStepProps> = ({ onHide }) => {
  const { projectName, projectType, description } = useProject()
  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [error, setError] = useState<string>()

  const handleCreateProject = useCallback(() => {
    if (projectName && projectType !== undefined) {
      createProject({
        name: projectName,
        description: `${description}`,
        annotation_type: projectType,
        license: '',
      }).catch((e) => {
        setError(e.data.message)
      })
    }
    onHide()
  }, [projectName, projectType, description, createProject, onHide])

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.focus()
    }
  }, [])

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !isCreating &&
        projectName &&
        projectType !== undefined
      ) {
        event.preventDefault()
        handleCreateProject()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [projectName, projectType, isCreating, handleCreateProject])

  const isValid = projectName && projectType !== undefined

  return (
    <Row className="justify-content-center">
      <Toast
        show={!!error}
        onClose={() => setError(undefined)}
        className="position-fixed top-50 start-50 translate-middle"
        style={{
          backgroundColor: '#2b2b2b',
          border: '1px solid #dc3545',
          boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
          minWidth: '400px',
          padding: '0',
          zIndex: 9999,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            backgroundColor: '#dc3545',
            padding: '10px 15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <strong className="text-white">Error</strong>
          <Button
            variant="link"
            className="p-0 text-white"
            onClick={() => setError(undefined)}
            style={{ fontSize: '20px', textDecoration: 'none' }}
          >
            ×
          </Button>
        </div>
        <Toast.Body className="text-light p-0 py-3 text-center">{error}</Toast.Body>
      </Toast>
      <Col xs={12} md={8}>
        <Card
          className="border-0 mb-4"
          style={{
            backgroundColor: '#2b2b2b',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Card.Header
            className="border-0 py-3"
            style={{ backgroundColor: 'rgba(13, 110, 253, 0.15)' }}
          >
            <h5 className="mb-0 text-primary">Project Summary</h5>
          </Card.Header>
          <Card.Body className="p-4">
            <dl className="row mb-0">
              <dt className="col-sm-4 text-light opacity-75">Project Name</dt>
              <dd className="col-sm-8 mb-3 text-light">{projectName || '—'}</dd>

              <dt className="col-sm-4 text-light opacity-75">Project Type</dt>
              <dd className="col-sm-8 mb-3 text-light">
                {projectType ? toTitleCase(projectType) : '—'}
              </dd>

              <dt className="col-sm-4 text-light opacity-75">Description</dt>
              <dd className="col-sm-8 mb-3 text-light">{description || '—'}</dd>

              <dt className="col-sm-4 text-light opacity-75">Creation Date</dt>
              <dd className="col-sm-8 text-light">{new Date().toLocaleDateString()}</dd>
            </dl>
          </Card.Body>
        </Card>

        <div className="d-flex flex-column align-items-center text-center">
          {!isValid && (
            <div className="mb-3 text-danger d-flex align-items-center">
              <AlertCircle size={20} className="me-2" />
              <span>Please complete all required fields before creating the project</span>
            </div>
          )}

          <Button
            ref={buttonRef}
            onClick={handleCreateProject}
            disabled={!isValid || isCreating}
            variant="primary"
            size="lg"
            className="px-5 py-3"
          >
            {isCreating ? (
              'Creating Project...'
            ) : (
              <span className="d-flex align-items-center">
                <Check size={20} className="me-2" />
                Create Project
              </span>
            )}
          </Button>
        </div>
      </Col>
    </Row>
  )
}

export default CPCreateProjectStep
